import { t } from "@lingui/macro";
import { auth, db, signInWithGoogle } from "@repo/ui";
import {
	collection,
	doc,
	getDocs,
	query,
	serverTimestamp,
	setDoc,
	where,
} from "firebase/firestore";
import {
	AlertCircle,
	CheckCircle2,
	Clock,
	Globe,
	Languages,
	Loader2,
	Phone,
	User as UserIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useData } from "../contexts/DataContext";
import { getContentFieldProps } from "../utils/textDirection";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

export function RegisterPage() {
	const { language, setLanguage } = useData();

	// State
	const [regName, setRegName] = useState("");
	const [regEmail, setRegEmail] = useState("");
	const [regCompanyWebsite, setRegCompanyWebsite] = useState("");
	const [regGoogleUid, setRegGoogleUid] = useState<string | null>(null);
	const [isSubmittingReg, setIsSubmittingReg] = useState(false);
	const [isGoogleLoading, setIsGoogleLoading] = useState(false);
	const [mode, setMode] = useState<"form" | "pending">("form");
	const [error, setError] = useState("");
	const [infoMessage, setInfoMessage] = useState("");

	// Apply RTL when Hebrew is selected
	useEffect(() => {
		document.documentElement.dir = language === "he" ? "rtl" : "ltr";
		document.documentElement.lang = language;
	}, [language]);

	const checkRegistrationState = async (
		userEmail: string,
		userName: string,
		googleUid: string,
	) => {
		try {
			const q = query(
				collection(db, "registrationRequests"),
				where("email", "==", userEmail.toLowerCase().trim()),
			);
			const snap = await getDocs(q);

			if (!snap.empty) {
				setMode("pending");
			} else {
				setRegEmail(userEmail);
				setRegName(userName);
				setRegGoogleUid(googleUid);
				setMode("form");
			}
		} catch (err) {
			console.error("Failed to check registration state", err);
			setRegEmail(userEmail);
			setRegName(userName);
			setRegGoogleUid(googleUid);
			setMode("form");
		}
	};

	const handleGoogleSignIn = async () => {
		setIsGoogleLoading(true);
		setError("");
		setInfoMessage("");

		try {
			const userCredential = await signInWithGoogle();
			if (!userCredential) return;
			const userObj = userCredential.user;
			const userEmail = userObj.email || "";
			const userName = userObj.displayName || "";
			const googleUid = userObj.uid;
			await checkRegistrationState(userEmail, userName, googleUid);
		} catch (err: unknown) {
			if (
				(err as { code?: unknown } | null)?.code !== "auth/popup-closed-by-user"
			) {
				console.error(err);
				setError(
					(err as { message?: string } | null)?.message ||
						t({
							id: "googleSignInFailed",
							message: "Google sign in failed. Please try again.",
						}),
				);
			}
		} finally {
			setIsGoogleLoading(false);
		}
	};

	const handleRegistrationSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!regEmail || !regName || !regCompanyWebsite) return;

		setIsSubmittingReg(true);
		setError("");

		try {
			const cleanEmail = regEmail.trim().toLowerCase();
			const cleanName = regName.trim();
			const cleanWebsite = regCompanyWebsite.trim();

			const regRef = doc(collection(db, "registrationRequests"));
			await setDoc(regRef, {
				id: regRef.id,
				email: cleanEmail,
				name: cleanName,
				companyWebsite: cleanWebsite,
				googleUid: regGoogleUid || null,
				status: "pending",
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp(),
			});

			const actionRef = doc(collection(db, "adminActions"));
			await setDoc(actionRef, {
				id: actionRef.id,
				companyId: "",
				subscriptionId: "",
				type: "registration_review",
				title: `Review registration for ${cleanName} (${cleanEmail})`,
				status: "open",
				registrationRequestId: regRef.id,
				email: cleanEmail,
				name: cleanName,
				companyWebsite: cleanWebsite,
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp(),
			});

			setMode("pending");
		} catch (err: unknown) {
			console.error("Failed to submit registration", err);
			setError(
				(err as { message?: string } | null)?.message ||
					t({
						id: "registrationSubmissionFailed",
						message: "Failed to submit registration. Please try again.",
					}),
			);
		} finally {
			setIsSubmittingReg(false);
		}
	};

	const handleBackToLogin = async () => {
		try {
			await auth.signOut();
		} catch (e) {
			console.error("Failed to sign out", e);
		}
		window.history.pushState({}, "", "/");
		window.dispatchEvent(new PopStateEvent("popstate"));
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
			<Card className="w-full max-w-md">
				<CardHeader className="text-center">
					<div className="flex justify-end mb-2">
						<Select
							value={language}
							onValueChange={(value: "en" | "he") => setLanguage(value)}
						>
							<SelectTrigger className="w-[140px]">
								<Languages
									className={`w-4 h-4 ${language === "he" ? "ml-2" : "mr-2"}`}
								/>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="en">
									{t({ id: "english", message: "English" })}
								</SelectItem>
								<SelectItem value="he">
									{t({ id: "hebrew", message: "Hebrew (עברית)" })}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-sm bg-accent">
						<Phone className="h-6 w-6 text-accent-foreground" />
					</div>
					<CardTitle className="text-2xl">
						{t({ id: "appName", message: "Provoize" })}
					</CardTitle>
					<CardDescription>
						{mode === "pending"
							? t({
									id: "registrationPendingTitleDesc",
									message: "Registration status update",
								})
							: t({
									id: "registrationTitleDesc",
									message: "Submit your details to request access",
								})}
					</CardDescription>
				</CardHeader>

				<CardContent>
					{mode === "form" && (
						<div className="space-y-4">
							<Button
								type="button"
								variant="outline"
								className="w-full flex items-center justify-center gap-2 py-5"
								onClick={handleGoogleSignIn}
								disabled={isGoogleLoading}
							>
								{isGoogleLoading ? (
									<Loader2 className="w-4 h-4 animate-spin" />
								) : (
									<svg className="w-5 h-5" viewBox="0 0 24 24">
										<path
											fill="#4285F4"
											d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
										/>
										<path
											fill="#34A853"
											d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
										/>
										<path
											fill="#FBBC05"
											d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
										/>
										<path
											fill="#EA4335"
											d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
										/>
									</svg>
								)}
								<span>
									{t({
										id: "signInWithGoogle",
										message: "Sign in with Google",
									})}
								</span>
							</Button>

							<div className="relative my-4">
								<div className="absolute inset-0 flex items-center">
									<div className="w-full border-t border-border" />
								</div>
								<div className="relative flex justify-center text-xs">
									<span className="bg-card px-2 text-muted-foreground">
										{t({ id: "orDivider", message: "Or continue with email" })}
									</span>
								</div>
							</div>

							<form onSubmit={handleRegistrationSubmit} className="space-y-4">
								{regGoogleUid ? (
									<div className="mb-2 rounded-md border border-warn/30 bg-warn-tint p-3 text-xs text-foreground">
										{t({
											id: "unrecognizedAccountNotice",
											message:
												"Your Google account is not yet registered. Please complete the registration request form below.",
										})}
									</div>
								) : null}

								<div className="space-y-2">
									<Label htmlFor="regName">
										{t({ id: "fullNameLabel", message: "Full Name" })}
									</Label>
									<div className="relative">
										<UserIcon className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
										<Input
											id="regName"
											type="text"
											{...getContentFieldProps(
												regName,
												language === "he" ? "rtl" : "ltr",
												"ps-9",
											)}
											placeholder={t({
												id: "enterNamePlaceholder",
												message: "Enter your full name",
											})}
											value={regName}
											onChange={(e) => setRegName(e.target.value)}
											required
										/>
									</div>
								</div>

								<div className="space-y-2">
									<Label htmlFor="regEmail">
										{t({ id: "emailLabel", message: "Email Address" })}
									</Label>
									<Input
										id="regEmail"
										type="email"
										dir="ltr"
										className="text-left"
										value={regEmail}
										onChange={(e) => setRegEmail(e.target.value)}
										required
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="regCompanyWebsite">
										{t({
											id: "companyWebsiteLabel",
											message: "Company Website",
										})}
									</Label>
									<div className="relative">
										<Globe className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
										<Input
											id="regCompanyWebsite"
											type="url"
											dir="ltr"
											className="ps-9 text-left"
											placeholder="https://example.com"
											value={regCompanyWebsite}
											onChange={(e) => setRegCompanyWebsite(e.target.value)}
											required
										/>
									</div>
								</div>

								<Button
									type="submit"
									className="w-full"
									disabled={isSubmittingReg}
								>
									{isSubmittingReg ? (
										<Loader2 className="w-4 h-4 animate-spin mr-2" />
									) : null}
									{t({
										id: "submitRegistrationBtn",
										message: "Submit Registration",
									})}
								</Button>

								<Button
									type="button"
									variant="ghost"
									className="w-full text-xs text-muted-foreground"
									onClick={handleBackToLogin}
								>
									{t({ id: "backToLoginBtn", message: "Back to Sign In" })}
								</Button>
							</form>
						</div>
					)}

					{mode === "pending" && (
						<div className="text-center space-y-4 py-4">
							<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warn-tint text-warn">
								<Clock className="w-6 h-6" />
							</div>
							<h3 className="text-lg font-bold text-foreground">
								{t({
									id: "registrationSubmittedTitle",
									message: "Registration Submitted",
								})}
							</h3>
							<p className="text-sm leading-relaxed text-muted-foreground">
								{t({
									id: "registrationSubmittedDesc",
									message:
										"Thank you! Your registration request has been received and is currently being reviewed by an administrator. You will receive access once approved.",
								})}
							</p>
							<Button
								type="button"
								variant="outline"
								className="w-full mt-4"
								onClick={handleBackToLogin}
							>
								{t({ id: "backToLoginBtn", message: "Back to Sign In" })}
							</Button>
						</div>
					)}

					{error && (
						<div className="mt-4 flex items-center justify-center gap-2 rounded-md border border-destructive/30 bg-danger-tint p-3 text-sm text-danger-ink">
							<AlertCircle className="w-4 h-4 shrink-0" />
							<span>{error}</span>
						</div>
					)}

					{infoMessage && (
						<div className="mt-4 flex items-center justify-center gap-2 rounded-md border border-live/30 bg-live-tint p-3 text-sm text-foreground">
							<CheckCircle2 className="w-4 h-4 shrink-0" />
							<span>{infoMessage}</span>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
